//Built-in libraries

import { MapPinIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
//Third party libraries
import { useLocation, useNavigate } from 'react-router';
import type { IGetProjectsServiceResData } from '@/api/Sherpa/Project/modules/ProjectCore/types';
//Custom components
import ProjectCard from '@/components/Sherpa/Projects/Project/ProjectCard';
import TableFooter from '@/components/Table/TableFooter';
import LoadingProjectsThumb from '@/components/UI/loading/LoadingProjectsThumb';
import { useResetProjectListPage } from '@/features/projects/components/ProjectsList/useResetProjectListPage.ts';
//Services
import { useProjectsData } from '@/hooks/ReactQueryHooks/useProjectQuery';
import { usePermission } from '@/hooks/usePermission';
import { workspaceSelector } from '@/store';
import { notifyUser } from '@/store/Toast';
//Assets and Miscellaneous
import type { FiltersInterface } from '@/types/Miscellaneous';
import { isObjectEmpty } from '@/utils';

//Component Begin
const DEFAULT_CARDS_PER_PAGE = 8;
const CARDS_PER_PAGE_OPTIONS = [8, 12, 24, 36, 48];

export const CardViewProjectsList = ({ filters }: { filters: FiltersInterface }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { hasPermission } = usePermission();
  const { currentWorkspace } = useSelector(workspaceSelector);

  const [perPage, setPerPage] = useState<number>(DEFAULT_CARDS_PER_PAGE);
  const [page, setPage] = useState<number>(1);

  const fetchProjects = useProjectsData({
    params: { perPage, page, ...filters },
  });
  const projects = fetchProjects?.data?.data;
  const totalProjects = fetchProjects?.data?.meta?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(Number(totalProjects) / perPage));
  const showingCount = Math.min(page * perPage, totalProjects);
  //const totalProjects = fetchProjects?.data?.meta?.total;
  //const totalPages = Math.ceil(Number(totalProjects) / 12);
  useResetProjectListPage(filters, setPage);

  const onPageChangeHandler = (direction: string) => {
    if (direction === 'prev') {
      setPage((prevState) => (prevState > 1 ? prevState - 1 : 1));
    } else if (direction === 'next') {
      setPage((prevState) => (prevState >= totalPages ? totalPages : prevState + 1));
    }
  };

  const projectDetailViewHandler = (projectId: number) => {
    if (hasPermission(['READ_PROJECT'])) {
      navigate(`${location.pathname}/${projectId}`, { state: location });
    } else {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'You do not have permission to read project details',
          toastType: 'info',
        })
      );
    }
  };

  return (
    <>
      {fetchProjects.isLoading && <LoadingProjectsThumb />}
      {!fetchProjects.isLoading && projects && totalProjects > 0 && (
        <>
          <div className='my-5'>
            <p className='mb-2'>
              Showing {showingCount} of {totalProjects} Projects
            </p>
          </div>
          <section className='mb-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5'>
            {projects.map((p: IGetProjectsServiceResData) => (
              <div key={p.id} onClick={() => projectDetailViewHandler(p.id)}>
                <ProjectCard project={p} />
              </div>
            ))}
          </section>
          <TableFooter
            currentPage={page}
            totalPage={totalPages}
            perPage={perPage}
            perPageOptions={CARDS_PER_PAGE_OPTIONS}
            perPageHandler={(e) => {
              setPerPage(Number(e.target.value));
              setPage(1);
            }}
            pageChangeHandler={onPageChangeHandler}
          />
        </>
      )}
      {!fetchProjects.isLoading && !isObjectEmpty(filters) && totalProjects === 0 && (
        <section>
          <div className='w-full flex justify-center items-center text-center h-screen/2 bg-white p-4'>
            <div>
              <div className='flex justify-center items-center gap-2 text-primaryColor-normal'>
                <MapPinIcon className='h-8 w-8' />
                <h1>{totalProjects} Projects</h1>
              </div>
              <p>
                There are {totalProjects} projects matching your criteria in{' '}
                {currentWorkspace ? <span className=' text-primaryColor-normal'>{currentWorkspace?.name}</span> : 'all'} program.
              </p>
            </div>
          </div>
        </section>
      )}
    </>
  );
};
